import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { tripsApi } from '../../../api/trips.api';
import { useDateLocale } from '../../../utils/dateLocale';
import { parseLocalDateRange } from '../../../utils/parseDateRange';

export interface DateRange { startDate: string; endDate: string; nights: number }

/**
 * The dates answer: free text or the calendar. Typing only ever fills the
 * calendar in; what the calendar shows is what gets submitted. Text the local
 * parser can't read goes to the AI parser on submit.
 */
export function useDatesAnswer(onApply: (range: DateRange) => void) {
  const { t, i18n } = useTranslation();
  const locale = useDateLocale();
  const [text, setText] = useState('');
  const [sel, setSel] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo<DateRange | null>(() => {
    if (!sel.start || !sel.end) return null;
    const nights = differenceInCalendarDays(parseISO(sel.end), parseISO(sel.start));
    return nights > 0 ? { startDate: sel.start, endDate: sel.end, nights } : null;
  }, [sel]);

  function onTextChange(value: string) {
    setText(value);
    setError(null);
    const parsed = parseLocalDateRange(value, { locale, dayFirst: !i18n.language.startsWith('en') });
    setSel(parsed ? { start: parsed.startDate, end: parsed.endDate } : { start: null, end: null });
  }

  function onCalendarChange(start: string, end: string | null) {
    setSel({ start, end });
    setText('');
    setError(null);
  }

  async function submit() {
    if (loading) return;
    if (range) { onApply(range); return; }
    const typed = text.trim();
    if (!typed) return;
    setLoading(true);
    setError(null);
    try {
      onApply(await tripsApi.parseDates(typed));
    } catch {
      setError(t('dashboard.newTripWizard.datesParseFailed'));
    } finally {
      setLoading(false);
    }
  }

  return { text, sel, range, loading, error, canSubmit: !!range || !!text.trim(), onTextChange, onCalendarChange, submit };
}

export type DatesAnswer = ReturnType<typeof useDatesAnswer>;
